import React, { useState } from 'react';
import { Card, CardMedia, CardContent, Typography, Button, Rating, Box, Snackbar, Alert } from '@mui/material';
import { Favorite, FavoriteBorder, OpenInNew, Share } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Event } from '../types';
import { eventAPI } from '../services/api';
import { useFavorites } from '../contexts/FavoritesContext';
import { generateEventUrl } from '../utils/urlUtils';

interface EventCardProps {
  event: Event;
  onCardClick?: (event: Event) => void;
  variant?: 'default' | 'homepage';
}

const EventCard: React.FC<EventCardProps> = ({ event, onCardClick, variant = 'default' }) => {
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const navigate = useNavigate();
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
  const isEventFavorite = isFavorite(event.uniqueId);

  const handleViewClick = async () => {
    try {
      await eventAPI.incrementClick(event.uniqueId);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error tracking click:', error);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEventFavorite) {
      removeFromFavorites(event.uniqueId);
    } else {
      addToFavorites(event);
      setSaveSnackbarOpen(true);
    }
  };

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(event);
    } else {
      const eventUrl = generateEventUrl(event);
      navigate(eventUrl);
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const shareData = {
      title: event.eventTitle,
      text: event.subhead || `Experience ${event.eventTitle}`,
      url: event.affiliateUrl,
    };

    // Check if device supports native sharing
    if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Desktop: Copy to clipboard
      try {
        await navigator.clipboard.writeText(event.affiliateUrl);
        setShareSnackbarOpen(true);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = event.affiliateUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShareSnackbarOpen(true);
      }
    }
  };

  return (
    <Card 
      onClick={handleCardClick}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 1.5,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.8)',
          '& .event-media': {
            transform: 'scale(1.1)',
          },
          '& .event-button': {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
          },
          '& .event-overlay': {
            opacity: 1,
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03), rgba(168, 85, 247, 0.03))',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          zIndex: 1,
        },
        '&:hover::before': {
          opacity: 1,
        }
      }}
    >
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          height="220"
          image={event.imageUrl}
          alt={event.eventTitle}
          className="event-media"
          sx={{ 
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            filter: 'brightness(1.05) saturate(1.1)',
          }}
        />
        <Box
          className="event-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
          }}
        />
      </Box>
      
      <CardContent 
        sx={{ 
          flexGrow: 1, 
          p: 3,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Typography 
          gutterBottom 
          variant="h6" 
          component="div"
          sx={{
            fontWeight: 700,
            fontSize: '1.1rem',
            lineHeight: 1.3,
            mb: 1.5,
            color: '#1f2937',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {event.eventTitle}
        </Typography>
        
        {event.price && (
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#059669',
              fontWeight: 700,
              fontSize: '1.1rem',
              mb: 0.5,
            }}
          >
            {event.price}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Rating 
            value={event.starRating} 
            readOnly 
            precision={0.5} 
            size="small"
            sx={{ 
              color: '#f59e0b',
              '& .MuiRating-iconFilled': {
                color: '#f59e0b',
              }
            }}
          />
          <Typography 
            variant="body2" 
            sx={{ 
              ml: 0.5,
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '0.75rem',
            }}
          >
            {event.starRating}
          </Typography>
        </Box>
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#6b7280',
            lineHeight: 1.6,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {event.subhead}
        </Typography>
      </CardContent>
      
      <Box sx={{ p: 2, pt: 0, position: 'relative', zIndex: 2, display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Button
          size="small"
          variant="contained"
          onClick={handleViewClick}
          sx={{
            fontSize: '0.75rem',
            textTransform: 'none',
            flex: variant === 'homepage' ? 2 : 1,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            },
            py: 1,
            borderRadius: 1.5,
          }}
        >
          Book Now ⇗
        </Button>
        <Button
          size="small"
          variant={isEventFavorite ? 'contained' : 'outlined'}
          onClick={handleFavoriteClick}
          startIcon={variant === 'homepage' ? undefined : (isEventFavorite ? <Favorite sx={{ fontSize: '0.9rem' }} /> : <FavoriteBorder sx={{ fontSize: '0.9rem' }} />)}
          sx={{
            background: isEventFavorite ? '#ef4444' : 'transparent',
            color: isEventFavorite ? 'white' : '#ef4444',
            borderColor: '#ef4444',
            fontSize: '0.75rem',
            textTransform: 'none',
            minWidth: variant === 'homepage' ? 'auto' : 'auto',
            '&:hover': {
              background: isEventFavorite ? '#dc2626' : 'rgba(239, 68, 68, 0.1)',
              borderColor: '#dc2626',
            },
            py: 1,
            px: variant === 'homepage' ? 1 : 1.5,
            borderRadius: 1.5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {variant === 'homepage' ? (isEventFavorite ? <Favorite sx={{ fontSize: '0.9rem' }} /> : <FavoriteBorder sx={{ fontSize: '0.9rem' }} />) : (isEventFavorite ? 'Saved' : 'Save')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleShare}
          startIcon={variant === 'homepage' ? undefined : <Share sx={{ fontSize: '0.9rem' }} />}
          sx={{
            color: '#6366f1',
            borderColor: '#6366f1',
            fontSize: '0.75rem',
            textTransform: 'none',
            minWidth: 'auto',
            '&:hover': {
              background: 'rgba(99, 102, 241, 0.1)',
              borderColor: '#4f46e5',
            },
            py: 1,
            px: variant === 'homepage' ? 1 : 1.5,
            borderRadius: 1.5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {variant === 'homepage' ? <Share sx={{ fontSize: '0.9rem' }} /> : 'Share'}
        </Button>
      </Box>
      
      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setShareSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShareSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={saveSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSaveSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSaveSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Saved to favorites!
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EventCard;